<template>
  <div>
    <section class="hero">
      <div class="hero-body">
        <div class="titulo-con-guia">
          <p class="title">{{ esAdmin ? t("Promotores QR", "QR Promoters") : t("Mi equipo", "My team") }}</p>
          <GuiaRecorrido ref="guia" id="qr-promotores" :pasos="pasosGuia" />
        </div>
        <p class="subtitle is-6">
          {{ esAdmin
            ? t("Cada promotor tiene un QR único: las ventas que entran por él se le atribuyen. Un supervisor ve las ventas de los agentes a su cargo.", "Each promoter has a unique QR: sales through it are attributed to them. A supervisor sees the sales of the agents under them.")
            : t("Tú y los agentes a tu cargo. Cada uno vende con su propio QR.", "You and the agents under you. Each one sells with their own QR.") }}
        </p>
      </div>
    </section>

    <div class="level">
      <div class="level-left" :data-guia="esAdmin ? 'p-filtro' : null">
        <b-field v-if="esAdmin" :label="t('Equipo', 'Team')" label-position="on-border" class="mb-0">
          <b-select v-model="filtroEquipo">
            <option value="todos">{{ t("Todos", "All") }}</option>
            <option value="supervisores">{{ t("Solo supervisores", "Supervisors only") }}</option>
            <option value="independientes">{{ t("Agentes sin supervisor", "Agents without supervisor") }}</option>
            <option value="establecimientos">{{ t("Establecimientos", "Businesses") }}</option>
            <option v-for="s in supervisores" :key="s._id" :value="s._id">{{ t("Equipo de", "Team of") }} {{ nombre(s) }}</option>
          </b-select>
        </b-field>
      </div>
      <div class="level-right">
        <div class="buttons">
          <span data-guia="p-establecimiento" class="mr-2"><b-button type="is-primary is-light" icon-left="store" icon-pack="fas" @click="abrirEstablecimiento(null)">{{ t("Agregar establecimiento", "Add business") }}</b-button></span>
          <span data-guia="p-agregar"><b-button type="is-primary" icon-left="user-plus" icon-pack="fas" @click="abrirAlta">{{ esAdmin ? t("Agregar promotor", "Add promoter") : t("Agregar agente", "Add agent") }}</b-button></span>
        </div>
      </div>
    </div>

    <b-table :data="promotoresVisibles" :loading="cargando" striped hoverable narrowed>
      <b-table-column :label="t('Promotor', 'Promoter')" v-slot="props">
        <strong>{{ nombre(props.row) }}</strong>
        <p v-if="props.row.Tipo === 'establecimiento'" class="is-size-7 has-text-grey">
          {{ [props.row.Establecimiento && props.row.Establecimiento.Ciudad, props.row.Establecimiento && props.row.Establecimiento.Contacto, props.row.Telefono].filter(Boolean).join(" · ") || t("Sin datos de contacto", "No contact details") }}
        </p>
        <p v-else class="is-size-7 has-text-grey">
          {{ props.row.Usuario && props.row.Usuario.Usuario }} · {{ props.row.Usuario && props.row.Usuario.Email }}<span v-if="props.row.Telefono"> · {{ props.row.Telefono }}</span>
        </p>
      </b-table-column>
      <b-table-column :label="t('Tipo', 'Type')" v-slot="props">
        <template v-if="props.row.Tipo === 'supervisor'">
          <b-tag type="is-info is-light">{{ t("Supervisor", "Supervisor") }}</b-tag>
          <p class="is-size-7 has-text-grey">{{ resumenEquipo(props.row) }}</p>
        </template>
        <template v-else>
          <b-tag v-if="props.row.Tipo === 'establecimiento'" type="is-warning is-light">{{ t("Establecimiento", "Business") }}</b-tag>
          <b-tag v-else type="is-light">{{ t("Agente", "Agent") }}</b-tag>
          <p v-if="esAdmin" class="is-size-7 has-text-grey">{{ props.row.SupervisorNombre || t("Sin supervisor", "No supervisor") }}</p>
          <p v-if="props.row.SupervisorId" class="is-size-7 has-text-grey">
            {{ Array.isArray(props.row.CampanaIds) ? `${props.row.CampanaIds.length} ${props.row.CampanaIds.length === 1 ? t("campaña", "campaign") : t("campañas", "campaigns")}` : t("Todas las campañas del equipo", "All team campaigns") }}
          </p>
        </template>
      </b-table-column>
      <b-table-column :label="t('Código', 'Code')" v-slot="props">
        <code>{{ props.row.Codigo }}</code>
      </b-table-column>
      <b-table-column :label="t('Link del QR', 'QR link')" v-slot="props">
        <a :href="props.row.Url" target="_blank" rel="noopener" class="is-size-7" data-guia="p-link">{{ props.row.Url }}</a>
      </b-table-column>
      <b-table-column :label="t('Alta', 'Added')" v-slot="props">
        <span class="is-size-7">{{ fecha(props.row.FechaCreacion) }}</span>
      </b-table-column>
      <b-table-column :label="t('Activo', 'Active')" v-slot="props" centered>
        <!-- Un supervisor no se puede desactivar a sí mismo: solo a sus agentes -->
        <span :data-guia="puedeCambiar(props.row) ? 'p-activo' : null"><b-switch :value="props.row.Activo" type="is-success" :disabled="!puedeCambiar(props.row)" @input="(v) => cambiarEstado(props.row, v)"></b-switch></span>
      </b-table-column>
      <b-table-column v-slot="props" centered>
        <div class="buttons is-centered are-small mb-0 botones-fila" style="flex-wrap: nowrap">
          <span data-guia="p-ver-qr"><b-button size="is-small" icon-left="qrcode" icon-pack="fas" type="is-info is-light" @click="verQr(props.row)">{{ t("Ver QR", "View QR") }}</b-button></span>
          <span data-guia="p-cartel"><b-button size="is-small" icon-left="print" icon-pack="fas" type="is-light" :title="t('Cartel para imprimir', 'Printable sign')" @click="verCartel(props.row)"></b-button></span>
          <b-button v-if="props.row.Tipo === 'establecimiento'" size="is-small" icon-left="pen" icon-pack="fas" type="is-light" :title="t('Datos del establecimiento', 'Business details')" @click="abrirEstablecimiento(props.row)"></b-button>
          <span v-if="props.row.SupervisorId" data-guia="p-campanas"><b-button size="is-small" icon-left="bullhorn" icon-pack="fas" type="is-light" :title="t('Campañas que vende', 'Campaigns they sell')" @click="abrirCampanas(props.row)"></b-button></span>
          <span v-if="esAdmin" data-guia="p-equipo"><b-button size="is-small" icon-left="sitemap" icon-pack="fas" type="is-light" :title="t('Tipo y supervisor', 'Type and supervisor')" @click="abrirEquipo(props.row)"></b-button></span>
        </div>
      </b-table-column>
      <template #empty>
        <p class="has-text-centered has-text-grey">{{ t("Todavía no hay promotores", "No promoters yet") }}</p>
      </template>
    </b-table>

    <!-- Alta de promotor -->
    <b-modal v-model="alta.abierto" has-modal-card trap-focus>
      <div class="modal-card" style="width: 520px; max-width: 100%">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ esAdmin ? t("Agregar promotor", "Add promoter") : t("Agregar agente", "Add agent") }}</p>
          <button type="button" class="delete" @click="alta.abierto = false"></button>
        </header>
        <!-- Paso 1: datos del promotor -->
        <section class="modal-card-body" v-if="!alta.creado">
          <b-message v-if="!esAdmin" type="is-info" size="is-small">
            {{ t("El agente quedará en tu equipo: verás sus ventas y podrás desactivar su QR.", "The agent will join your team: you will see their sales and can disable their QR.") }}
          </b-message>
          <div class="columns is-multiline">
            <template v-if="esAdmin">
              <div class="column is-6">
                <b-field :label="t('Tipo', 'Type')">
                  <b-select v-model="alta.form.Tipo" expanded>
                    <option value="agente">{{ t("Agente", "Agent") }}</option>
                    <option value="supervisor">{{ t("Supervisor (con agentes a su cargo)", "Supervisor (with agents)") }}</option>
                  </b-select>
                </b-field>
              </div>
              <div class="column is-6" v-if="alta.form.Tipo === 'agente'">
                <b-field :label="t('Supervisor', 'Supervisor')">
                  <b-select v-model="alta.form.SupervisorId" expanded>
                    <option :value="null">{{ t("Sin supervisor", "No supervisor") }}</option>
                    <option v-for="s in supervisoresActivos" :key="s._id" :value="s._id">{{ nombre(s) }}</option>
                  </b-select>
                </b-field>
              </div>
            </template>
            <div class="column is-6">
              <b-field :label="t('Nombre *', 'First name *')"><b-input v-model="alta.form.Nombre" maxlength="60" :has-counter="false"></b-input></b-field>
            </div>
            <div class="column is-6">
              <b-field :label="t('Apellido *', 'Last name *')"><b-input v-model="alta.form.Apellido" maxlength="60" :has-counter="false"></b-input></b-field>
            </div>
            <div class="column is-12">
              <b-field :label="t('Correo *', 'Email *')" :message="t('Con este correo entra al backoffice.', 'They log in to the backoffice with this email.')">
                <b-input v-model="alta.form.Email" type="email" maxlength="120" :has-counter="false"></b-input>
              </b-field>
            </div>
            <div class="column is-12">
              <b-field :label="t('Teléfono (con código de país)', 'Phone (with country code)')">
                <b-input v-model="alta.form.Telefono" placeholder="+1 305 555 0123" icon="phone" icon-pack="fas"></b-input>
              </b-field>
            </div>
            <div class="column is-12">
              <b-field :label="t('Contraseña *', 'Password *')" :message="t('Mínimo 8 caracteres. Se la tendrás que compartir al promotor.', 'At least 8 characters. You will need to share it with the promoter.')">
                <!-- Visible a propósito: el admin tiene que compartírsela al promotor -->
                <b-input v-model="alta.form.Password" expanded></b-input>
                <p class="control">
                  <b-button icon-left="sync" icon-pack="fas" @click="alta.form.Password = generarPassword()">{{ t("Generar", "Generate") }}</b-button>
                </p>
              </b-field>
            </div>
          </div>
        </section>

        <!-- Paso 2: listo, datos de acceso para compartir -->
        <section class="modal-card-body" v-else>
          <b-message type="is-success">
            {{ t("Promotor creado. Comparte estos datos de acceso con él (la contraseña no se vuelve a mostrar):", "Promoter created. Share these login details with them (the password won't be shown again):") }}
          </b-message>
          <div class="acceso-qr">
            <p><b>{{ t("Entra en", "Log in at") }}:</b> {{ urlBackoffice }}</p>
            <p><b>{{ t("Correo", "Email") }}:</b> {{ alta.creado.Acceso.Email }}</p>
            <p><b>{{ t("Contraseña", "Password") }}:</b> <code>{{ alta.form.Password }}</code></p>
            <p><b>{{ t("Su QR", "Their QR") }}:</b> <a :href="alta.creado.Url" target="_blank" rel="noopener">{{ alta.creado.Url }}</a></p>
          </div>
          <b-button class="mt-3" size="is-small" icon-left="copy" icon-pack="fas" @click="copiar(textoAcceso)">{{ t("Copiar todo", "Copy all") }}</b-button>
        </section>

        <footer class="modal-card-foot" style="justify-content: flex-end">
          <template v-if="!alta.creado">
            <b-button @click="alta.abierto = false">{{ t("Cancelar", "Cancel") }}</b-button>
            <b-button type="is-primary" :loading="alta.guardando" @click="crear">{{ esAdmin ? t("Crear promotor y QR", "Create promoter and QR") : t("Crear agente y QR", "Create agent and QR") }}</b-button>
          </template>
          <template v-else>
            <b-button @click="alta.abierto = false">{{ t("Cerrar", "Close") }}</b-button>
            <b-button type="is-primary" icon-left="qrcode" icon-pack="fas" @click="verCreado">{{ t("Ver su QR", "View their QR") }}</b-button>
          </template>
        </footer>
      </div>
    </b-modal>

    <!-- Alta y edición de establecimiento -->
    <b-modal v-model="estab.abierto" has-modal-card trap-focus>
      <div class="modal-card" style="width: 520px; max-width: 100%">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ estab.id ? t("Datos del establecimiento", "Business details") : t("Agregar establecimiento", "Add business") }}</p>
          <button type="button" class="delete" @click="estab.abierto = false"></button>
        </header>
        <section class="modal-card-body">
          <b-message v-if="!estab.id" type="is-info" size="is-small">
            {{ esAdmin
              ? t("Un establecimiento es un negocio con su QR en el mostrador. No entra al backoffice: sus registros los ve su supervisor.", "A business has its QR at the counter. It does not log in: its supervisor sees its registrations.")
              : t("El establecimiento quedará en tu equipo: verás sus registros y ventas, y podrás imprimir su cartel.", "The business will join your team: you will see its registrations and sales, and can print its sign.") }}
          </b-message>
          <div class="columns is-multiline">
            <div class="column is-12">
              <b-field :label="t('Nombre del establecimiento *', 'Business name *')"><b-input v-model="estab.form.Nombre" maxlength="80" :has-counter="false"></b-input></b-field>
            </div>
            <div class="column is-6">
              <b-field :label="t('Ciudad', 'City')"><b-input v-model="estab.form.Ciudad" maxlength="60" :has-counter="false"></b-input></b-field>
            </div>
            <div class="column is-6">
              <b-field :label="t('Contacto', 'Contact person')"><b-input v-model="estab.form.Contacto" maxlength="80" :has-counter="false"></b-input></b-field>
            </div>
            <div class="column is-12">
              <b-field :label="t('Dirección', 'Address')"><b-input v-model="estab.form.Direccion" maxlength="160" :has-counter="false"></b-input></b-field>
            </div>
            <div class="column is-6">
              <b-field :label="t('Teléfono (con código de país)', 'Phone (with country code)')"><b-input v-model="estab.form.Telefono" placeholder="+52 998 123 4567"></b-input></b-field>
            </div>
            <div class="column is-6" v-if="esAdmin && !estab.id">
              <b-field :label="t('Supervisor', 'Supervisor')">
                <b-select v-model="estab.form.SupervisorId" expanded>
                  <option :value="null">{{ t("Sin supervisor", "No supervisor") }}</option>
                  <option v-for="s in supervisoresActivos" :key="s._id" :value="s._id">{{ nombre(s) }}</option>
                </b-select>
              </b-field>
            </div>
          </div>
        </section>
        <footer class="modal-card-foot" style="justify-content: flex-end">
          <b-button @click="estab.abierto = false">{{ t("Cancelar", "Cancel") }}</b-button>
          <b-button type="is-primary" :loading="estab.guardando" @click="guardarEstablecimiento">{{ estab.id ? t("Guardar", "Save") : t("Crear establecimiento y QR", "Create business and QR") }}</b-button>
        </footer>
      </div>
    </b-modal>

    <!-- Tipo y supervisor (solo admin) -->
    <b-modal v-model="equipo.abierto" has-modal-card trap-focus>
      <div class="modal-card" style="width: 460px; max-width: 100%">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ t("Tipo y supervisor", "Type and supervisor") }} · {{ equipo.promotor && nombre(equipo.promotor) }}</p>
          <button type="button" class="delete" @click="equipo.abierto = false"></button>
        </header>
        <section class="modal-card-body">
          <b-field :label="t('Tipo', 'Type')">
            <!-- Un establecimiento no tiene usuario: no cambia de tipo -->
            <b-select v-model="equipo.Tipo" expanded :disabled="equipo.Tipo === 'establecimiento'">
              <option value="agente">{{ t("Agente", "Agent") }}</option>
              <option value="supervisor">{{ t("Supervisor (con agentes a su cargo)", "Supervisor (with agents)") }}</option>
              <option v-if="equipo.Tipo === 'establecimiento'" value="establecimiento">{{ t("Establecimiento", "Business") }}</option>
            </b-select>
          </b-field>
          <b-field v-if="equipo.Tipo !== 'supervisor'" :label="t('Supervisor', 'Supervisor')">
            <b-select v-model="equipo.SupervisorId" expanded>
              <option :value="null">{{ t("Sin supervisor", "No supervisor") }}</option>
              <option v-for="s in supervisoresActivos.filter((s) => s._id !== (equipo.promotor && equipo.promotor._id))" :key="s._id" :value="s._id">{{ nombre(s) }}</option>
            </b-select>
          </b-field>
          <p class="is-size-7 has-text-grey">
            {{ t("Las ventas ya hechas siguen contando para el supervisor que tenía al venderse; el cambio aplica a las ventas nuevas.", "Sales already made still count for the supervisor at the time of the sale; the change applies to new sales.") }}
          </p>
        </section>
        <footer class="modal-card-foot" style="justify-content: flex-end">
          <b-button @click="equipo.abierto = false">{{ t("Cancelar", "Cancel") }}</b-button>
          <b-button type="is-primary" :loading="equipo.guardando" @click="guardarEquipo">{{ t("Guardar", "Save") }}</b-button>
        </footer>
      </div>
    </b-modal>

    <!-- Campañas de un agente con supervisor -->
    <b-modal v-model="campanas.abierto" has-modal-card trap-focus>
      <div class="modal-card" style="width: 500px; max-width: 100%">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ t("Campañas", "Campaigns") }} · {{ campanas.promotor && nombre(campanas.promotor) }}</p>
          <button type="button" class="delete" @click="campanas.abierto = false"></button>
        </header>
        <section class="modal-card-body">
          <b-loading :is-full-page="false" :active="campanas.cargando"></b-loading>
          <div class="block">
            <b-radio v-model="campanas.todas" :native-value="true">{{ t("Todas las campañas del equipo", "All team campaigns") }}</b-radio>
            <b-radio v-model="campanas.todas" :native-value="false">{{ t("Solo estas", "Only these") }}</b-radio>
          </div>
          <template v-if="!campanas.todas">
            <p v-if="!campanas.disponibles.length && !campanas.cargando" class="has-text-grey is-size-7">
              {{ t("El equipo todavía no tiene campañas asignadas.", "The team has no campaigns assigned yet.") }}
            </p>
            <label v-for="c in campanas.disponibles" :key="c._id" class="opcion-campana">
              <input type="checkbox" v-model="campanas.elegidas" :value="c._id" />
              <span>
                {{ c.Nombre && c.Nombre[iso === "es" ? "es" : "en"] }}
                <span class="has-text-grey is-size-7">· ${{ c.Precio }} {{ (c.Moneda || "usd").toUpperCase() }}<span v-if="!c.Activa"> · {{ t("inactiva", "inactive") }}</span></span>
              </span>
            </label>
          </template>
          <p class="is-size-7 has-text-grey mt-3">
            {{ t("Su QR solo mostrará estas campañas. \"Todas\" incluye también las que el equipo reciba más adelante.", "Their QR will only show these campaigns. \"All\" also includes campaigns the team gets later.") }}
          </p>
        </section>
        <footer class="modal-card-foot" style="justify-content: flex-end">
          <b-button @click="campanas.abierto = false">{{ t("Cancelar", "Cancel") }}</b-button>
          <b-button type="is-primary" :loading="campanas.guardando" :disabled="campanas.cargando" @click="guardarCampanas">{{ t("Guardar", "Save") }}</b-button>
        </footer>
      </div>
    </b-modal>

    <!-- QR -->
    <b-modal v-model="qr.abierto" has-modal-card @close="cerrarQr">
      <div class="modal-card" style="width: 460px; max-width: 100%">
        <header class="modal-card-head">
          <p class="modal-card-title">QR · {{ qr.promotor && nombre(qr.promotor) }}</p>
          <button type="button" class="delete" @click="cerrarQr"></button>
        </header>
        <section class="modal-card-body has-text-centered">
          <b-loading :is-full-page="false" :active="qr.cargando"></b-loading>
          <img v-if="qr.imagen" :src="qr.imagen" alt="QR" style="width: 280px; height: 280px" />
          <p class="is-size-7 mt-2"><a :href="qr.promotor && qr.promotor.Url" target="_blank" rel="noopener">{{ qr.promotor && qr.promotor.Url }}</a></p>
          <b-message v-if="qr.promotor && !qr.promotor.Activo" type="is-warning" size="is-small" class="mt-3">
            {{ t("Este promotor está desactivado: su QR muestra 'no activo'.", "This promoter is disabled: their QR shows 'not active'.") }}
          </b-message>
        </section>
        <footer class="modal-card-foot" style="justify-content: center; flex-wrap: wrap; gap: 0.5rem">
          <b-button icon-left="download" icon-pack="fas" @click="bajar('png')">PNG</b-button>
          <b-button icon-left="print" icon-pack="fas" @click="bajar('svg')">{{ t("SVG (imprimir)", "SVG (print)") }}</b-button>
          <b-button icon-left="copy" icon-pack="fas" @click="copiar(qr.promotor.Url)">{{ t("Copiar link", "Copy link") }}</b-button>
        </footer>
      </div>
    </b-modal>
  </div>
</template>

<script>
import moment from "moment";
import apiQr, { mensajeError, descargar, imagenBlob } from "../../utils/apiQr";
import GuiaRecorrido from "../../components/GuiaRecorrido.vue";
import { pasosPromotores } from "./guiaQr";

export default {
  name: "PromotoresQr",
  components: { GuiaRecorrido },
  data() {
    return {
      iso: localStorage.getItem("iso") || "es",
      // Mismo criterio que el resto del backoffice (checkPermission): admin y ultrauser ven todo.
      // Si no, es un supervisor: la API solo le devuelve a él y a su equipo.
      esAdmin: ["admin", "ultrauser"].includes(localStorage.getItem("role")),
      promotores: [],
      cargando: false,
      filtroEquipo: "todos",
      alta: { abierto: false, form: {}, guardando: false, creado: null },
      equipo: { abierto: false, promotor: null, Tipo: "agente", SupervisorId: null, guardando: false },
      estab: { abierto: false, id: null, form: {}, guardando: false },
      campanas: { abierto: false, promotor: null, disponibles: [], todas: true, elegidas: [], cargando: false, guardando: false },
      qr: { abierto: false, promotor: null, imagen: null, cargando: false },
    };
  },
  computed: {
    pasosGuia() {
      return pasosPromotores(this.t, { esAdmin: this.esAdmin });
    },
    supervisores() {
      return this.promotores.filter((p) => p.Tipo === "supervisor");
    },
    supervisoresActivos() {
      return this.supervisores.filter((p) => p.Activo);
    },
    promotoresVisibles() {
      const f = this.filtroEquipo;
      if (f === "todos") return this.promotores;
      if (f === "supervisores") return this.supervisores;
      if (f === "independientes") return this.promotores.filter((p) => p.Tipo === "agente" && !p.SupervisorId);
      if (f === "establecimientos") return this.promotores.filter((p) => p.Tipo === "establecimiento");
      return this.promotores.filter((p) => p._id === f || p.SupervisorId === f);
    },
    urlBackoffice() {
      return window.location.origin;
    },
    textoAcceso() {
      const c = this.alta.creado;
      if (!c) return "";
      return [
        `${this.t("Entra en", "Log in at")}: ${this.urlBackoffice}`,
        `${this.t("Correo", "Email")}: ${c.Acceso.Email}`,
        `${this.t("Contraseña", "Password")}: ${this.alta.form.Password}`,
        `${this.t("Tu QR", "Your QR")}: ${c.Url}`,
      ].join("\n");
    },
  },
  created() {
    this.$bus.on("change-iso", () => (this.iso = localStorage.getItem("iso")));
    this.cargaInicial = this.cargar();
  },
  mounted() {
    // La guía se abre sola la primera vez, ya con la lista cargada
    this.cargaInicial.then(() => this.$nextTick(() => this.$refs.guia && this.$refs.guia.autoIniciar()));
  },
  methods: {
    t(es, en) {
      return this.iso === "es" ? es : en;
    },
    nombre(p) {
      const u = p.Usuario || {};
      return [u.Nombre, u.Apellido].filter(Boolean).join(" ") || u.Usuario || "";
    },
    fecha(ms) {
      return ms ? moment(ms).format("DD/MM/YYYY") : "";
    },
    // "2 agentes · 1 establecimiento"
    resumenEquipo(supervisor) {
      const equipo = this.promotores.filter((p) => p.SupervisorId === supervisor._id);
      const agentes = equipo.filter((p) => p.Tipo !== "establecimiento").length;
      const locales = equipo.length - agentes;
      const partes = [`${agentes} ${agentes === 1 ? this.t("agente", "agent") : this.t("agentes", "agents")}`];
      if (locales) partes.push(`${locales} ${locales === 1 ? this.t("establecimiento", "business") : this.t("establecimientos", "businesses")}`);
      return partes.join(" · ");
    },
    abrirEstablecimiento(promotor) {
      const e = (promotor && promotor.Establecimiento) || {};
      this.estab = {
        abierto: true,
        id: promotor ? promotor._id : null,
        form: { Nombre: e.Nombre || "", Ciudad: e.Ciudad || "", Direccion: e.Direccion || "", Contacto: e.Contacto || "", Telefono: (promotor && promotor.Telefono) || "", SupervisorId: null },
        guardando: false,
      };
    },
    async guardarEstablecimiento() {
      const f = this.estab.form;
      if (!f.Nombre.trim()) return this.$buefy.toast.open({ message: this.t("El nombre es obligatorio", "Name is required"), type: "is-warning" });
      const digitos = f.Telefono.replace(/\D/g, "");
      if (digitos && (digitos.length < 8 || digitos.length > 15)) return this.$buefy.toast.open({ message: this.t("El teléfono debe incluir el código de país", "Phone must include the country code"), type: "is-warning" });
      const datos = { Nombre: f.Nombre.trim(), Ciudad: f.Ciudad.trim(), Direccion: f.Direccion.trim(), Contacto: f.Contacto.trim(), Telefono: f.Telefono.trim() };
      try {
        this.estab.guardando = true;
        if (this.estab.id) {
          await apiQr.put(`/promotores/${this.estab.id}/establecimiento`, datos);
          this.estab.abierto = false;
          this.$buefy.toast.open({ message: this.t("Guardado", "Saved"), type: "is-success" });
          return this.cargar();
        }
        const { data } = await apiQr.post("/promotores", { Establecimiento: datos, ...(this.esAdmin ? { SupervisorId: f.SupervisorId } : {}) });
        this.estab.abierto = false;
        await this.cargar();
        // Recién creado: directo a su QR para descargarlo o imprimir el cartel
        const nuevo = this.promotores.find((p) => p._id === data._id);
        if (nuevo) this.verQr(nuevo);
      } catch (e) {
        this.$buefy.toast.open({ message: mensajeError(e, this.t("No se pudo guardar", "Could not save")), type: "is-danger" });
      } finally {
        this.estab.guardando = false;
      }
    },
    // El cartel es HTML: se abre en otra pestaña (abierta antes de pedirlo, para que no la bloquee el navegador)
    async verCartel(promotor) {
      const ventana = window.open("", "_blank");
      try {
        const { data } = await apiQr.get(`/promotores/${promotor._id}/cartel`, { responseType: "blob" });
        ventana.location = URL.createObjectURL(new Blob([data], { type: "text/html" }));
      } catch (e) {
        if (ventana) ventana.close();
        this.$buefy.toast.open({ message: mensajeError(e, this.t("No se pudo generar el cartel", "Could not generate the sign")), type: "is-danger" });
      }
    },
    // El admin cambia a cualquiera; el supervisor, solo a sus agentes
    puedeCambiar(promotor) {
      return this.esAdmin || Boolean(promotor.SupervisorId);
    },
    async abrirCampanas(promotor) {
      this.campanas = { abierto: true, promotor, disponibles: [], todas: true, elegidas: [], cargando: true, guardando: false };
      try {
        const { data } = await apiQr.get(`/promotores/${promotor._id}/campanas`);
        this.campanas.disponibles = data.disponibles;
        this.campanas.todas = !Array.isArray(data.CampanaIds);
        this.campanas.elegidas = data.CampanaIds || [];
      } catch (e) {
        this.campanas.abierto = false;
        this.$buefy.toast.open({ message: mensajeError(e, this.t("No se pudieron cargar las campañas", "Could not load campaigns")), type: "is-danger" });
      } finally {
        this.campanas.cargando = false;
      }
    },
    async guardarCampanas() {
      const c = this.campanas;
      if (!c.todas && !c.elegidas.length) return this.$buefy.toast.open({ message: this.t("Elige al menos una campaña", "Choose at least one campaign"), type: "is-warning" });
      try {
        c.guardando = true;
        await apiQr.put(`/promotores/${c.promotor._id}/campanas`, { CampanaIds: c.todas ? null : c.elegidas });
        c.abierto = false;
        this.$buefy.toast.open({ message: this.t("Guardado", "Saved"), type: "is-success" });
        this.cargar();
      } catch (e) {
        this.$buefy.toast.open({ message: mensajeError(e, this.t("No se pudo guardar", "Could not save")), type: "is-danger" });
      } finally {
        c.guardando = false;
      }
    },
    abrirEquipo(promotor) {
      this.equipo = { abierto: true, promotor, Tipo: promotor.Tipo || "agente", SupervisorId: promotor.SupervisorId || null, guardando: false };
    },
    async guardarEquipo() {
      try {
        this.equipo.guardando = true;
        const { data } = await apiQr.put(`/promotores/${this.equipo.promotor._id}/equipo`, { Tipo: this.equipo.Tipo, SupervisorId: this.equipo.SupervisorId });
        this.equipo.abierto = false;
        this.$buefy.toast.open({
          message: data.RolCambiado
            ? this.t("Guardado. Su rol cambió: tiene que cerrar sesión y volver a entrar para ver sus pantallas nuevas.", "Saved. Their role changed: they must log out and back in to see their new screens.")
            : this.t("Guardado", "Saved"),
          type: "is-success",
          duration: data.RolCambiado ? 7000 : 2500,
        });
        this.cargar();
      } catch (e) {
        this.$buefy.toast.open({ message: mensajeError(e, this.t("No se pudo guardar", "Could not save")), type: "is-danger" });
      } finally {
        this.equipo.guardando = false;
      }
    },
    async cargar() {
      try {
        this.cargando = true;
        const { data } = await apiQr.get("/promotores");
        this.promotores = data;
      } catch (error) {
        this.$buefy.toast.open({ message: mensajeError(error, this.t("Error al cargar promotores", "Error loading promoters")), type: "is-danger" });
      } finally {
        this.cargando = false;
      }
    },
    // Sin letras ni números que se confundan al dictarla o escribirla
    generarPassword() {
      const alfabeto = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
      const bytes = new Uint32Array(10);
      window.crypto.getRandomValues(bytes);
      return Array.from(bytes, (b) => alfabeto[b % alfabeto.length]).join("");
    },
    abrirAlta() {
      this.alta = {
        abierto: true,
        form: { Nombre: "", Apellido: "", Email: "", Telefono: "", Password: this.generarPassword(), Tipo: "agente", SupervisorId: null },
        guardando: false,
        creado: null,
      };
    },
    validarAlta() {
      const f = this.alta.form;
      if (!f.Nombre.trim() || !f.Apellido.trim()) return this.t("Nombre y apellido son obligatorios", "First and last name are required");
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(f.Email.trim())) return this.t("El correo no es válido", "Invalid email");
      const digitos = f.Telefono.replace(/\D/g, "");
      if (digitos && (digitos.length < 8 || digitos.length > 15)) return this.t("El teléfono debe incluir el código de país", "Phone must include the country code");
      if (f.Password.length < 8) return this.t("La contraseña debe tener al menos 8 caracteres", "Password must have at least 8 characters");
      return null;
    },
    async crear() {
      const error = this.validarAlta();
      if (error) return this.$buefy.toast.open({ message: error, type: "is-warning" });
      try {
        this.alta.guardando = true;
        const f = this.alta.form;
        const { data } = await apiQr.post("/promotores", {
          Nuevo: { Nombre: f.Nombre.trim(), Apellido: f.Apellido.trim(), Email: f.Email.trim(), Telefono: f.Telefono.trim(), Password: f.Password },
          // Un supervisor no los manda: la API pone al agente en su equipo
          ...(this.esAdmin ? { Tipo: f.Tipo, SupervisorId: f.Tipo === "agente" ? f.SupervisorId : null } : {}),
        });
        this.alta.creado = data;
        this.cargar();
      } catch (e) {
        this.$buefy.toast.open({ message: mensajeError(e, this.t("No se pudo crear el promotor", "Could not create the promoter")), type: "is-danger" });
      } finally {
        this.alta.guardando = false;
      }
    },
    verCreado() {
      const nuevo = this.promotores.find((p) => p._id === this.alta.creado._id);
      this.alta.abierto = false;
      if (nuevo) this.verQr(nuevo);
    },
    cambiarEstado(promotor, activo) {
      if (!this.puedeCambiar(promotor)) return;
      const accion = activo ? this.t("activar", "enable") : this.t("desactivar", "disable");
      this.$buefy.dialog.confirm({
        message: activo
          ? this.t(`¿Activar a ${this.nombre(promotor)}? Su QR volverá a funcionar.`, `Enable ${this.nombre(promotor)}? Their QR will work again.`)
          : this.t(`¿Desactivar a ${this.nombre(promotor)}? Su QR dejará de funcionar de inmediato.`, `Disable ${this.nombre(promotor)}? Their QR will stop working immediately.`),
        confirmText: accion.charAt(0).toUpperCase() + accion.slice(1),
        cancelText: this.t("Cancelar", "Cancel"),
        type: activo ? "is-success" : "is-danger",
        onConfirm: async () => {
          try {
            await apiQr.put(`/promotores/${promotor._id}/estado`, { Activo: activo });
            promotor.Activo = activo;
          } catch (error) {
            this.$buefy.toast.open({ message: mensajeError(error), type: "is-danger" });
          }
        },
        onCancel: () => this.cargar(),
      });
    },
    async verQr(promotor) {
      this.qr = { abierto: true, promotor, imagen: null, cargando: true };
      try {
        this.qr.imagen = await imagenBlob(`/promotores/${promotor._id}/qr`);
      } catch (error) {
        this.$buefy.toast.open({ message: mensajeError(error, this.t("No se pudo generar el QR", "Could not generate the QR")), type: "is-danger" });
      } finally {
        this.qr.cargando = false;
      }
    },
    cerrarQr() {
      if (this.qr.imagen) URL.revokeObjectURL(this.qr.imagen);
      this.qr = { abierto: false, promotor: null, imagen: null, cargando: false };
    },
    async bajar(formato) {
      const p = this.qr.promotor;
      const usuario = (p.Usuario && p.Usuario.Usuario) || p.Codigo;
      try {
        await descargar(`/promotores/${p._id}/qr`, `qr-${usuario}.${formato}`, formato === "svg" ? { formato: "svg" } : {});
      } catch (error) {
        this.$buefy.toast.open({ message: mensajeError(error), type: "is-danger" });
      }
    },
    async copiar(texto) {
      try {
        await navigator.clipboard.writeText(texto);
        this.$buefy.toast.open({ message: this.t("Link copiado", "Link copied"), type: "is-success" });
      } catch (e) {
        this.$buefy.toast.open({ message: texto, type: "is-info", duration: 6000 });
      }
    },
  },
};
</script>

<style scoped>
/* Los botones van dentro de <span data-guia> para la guía: el espacio entre ellos lo da el gap */
.botones-fila {
  gap: 0.4rem;
}
.botones-fila .button {
  margin: 0;
}
.opcion-campana {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.35rem 0;
  cursor: pointer;
}
.opcion-campana input {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
}
.acceso-qr {
  background: #f5f7fa;
  border-radius: 6px;
  padding: 0.8rem 1rem;
  line-height: 1.9;
  word-break: break-all;
}
</style>
